import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Stepper from '../components/Stepper';
import LanguageSelector from '../components/LanguageSelector';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Briefcase, ArrowLeft } from 'lucide-react';
import ErrorTracker from '../utils/errorTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const jobData = location.state?.job;

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);
  const [failedSubmission, setFailedSubmission] = useState(null);
  const [formData, setFormData] = useState({
    // Step 1: Personal Info
    full_name: '',
    email: '',
    phone: '',
    city: '',
    languages_spoken: [], // Array of 'english', 'spanish', 'other'
    other_language: '', // Text input if 'other' is selected
    
    // Step 2: Employment History
    most_recent_job: '',
    why_stopped_working: '',
    currently_working: null,
    current_work_type: '',
    current_work_days: '',
    looking_for: '',
    position_applying: jobData?.title || '',
    how_did_you_hear: '',
    
    // Step 3: Authorization & Background
    work_authorization: '',
    clean_background: null,
    
    // Step 4: Experience & Skills
    has_prior_experience: null,
    experience_description: '',
    
    // Step 5: Availability & Start Date
    days_option: '', // 'all' or 'specific'
    available_days: [],
    hours_option: '', // 'full-time', 'part-time', or 'custom'
    available_hours: '',
    when_can_start: '',
    start_date: '',
    
    // Step 6: Transportation & Preferences
    has_transportation: null,
    can_work_weekends: null,
    available_full_time: null,
    
    // Step 7: Additional
    additional_info: '',
    resume_file: null,
    preferred_language: language
  });

  const [errors, setErrors] = useState({});
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [fileName, setFileName] = useState('');

  // Check for failed submission on mount
  useEffect(() => {
    const failed = ErrorTracker.getFailedSubmission();
    if (failed) {
      setFailedSubmission(failed);
      setShowRecoveryPrompt(true);
    }
  }, []);

  const handleRecoverSubmission = () => {
    if (failedSubmission && failedSubmission.formData) {
      // Restore form data from failed submission
      setFormData(prev => ({
        ...prev,
        ...failedSubmission.formData
      }));
      setCurrentStep(7); // Go to last step for review
      setShowRecoveryPrompt(false);
      toast.info(t('submissionRecovered') || 'Previous submission recovered. Please review and submit again.');
    }
  };

  const handleDismissRecovery = () => {
    ErrorTracker.markFailedSubmissionRecovered();
    setShowRecoveryPrompt(false);
    setFailedSubmission(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePhoneChange = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    let formatted = '';
    if (digits.length > 0) {
      formatted = '(' + digits.substring(0, 3);
      if (digits.length >= 3) {
        formatted += ') ' + digits.substring(3, 6);
      }
      if (digits.length >= 6) {
        formatted += '-' + digits.substring(6, 10);
      }
    }
    
    setFormData(prev => ({ ...prev, phone: formatted }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const floridaCities = [
    'Miami', 'Miami Beach', 'Fort Lauderdale', 'Hollywood', 'Pembroke Pines',
    'Coral Springs', 'Miramar', 'Davie', 'Plantation', 'Sunrise',
    'Boca Raton', 'Deerfield Beach', 'Boynton Beach', 'Delray Beach',
    'West Palm Beach', 'Palm Beach Gardens', 'Wellington', 'Jupiter',
    'Hialeah', 'Kendall', 'Homestead', 'Key West', 'Naples', 'Tampa',
    'Orlando', 'Jacksonville', 'Tallahassee', 'St. Petersburg', 'Clearwater'
  ];

  const handleCityChange = (value) => {
    setFormData(prev => ({ ...prev, city: value }));
    
    if (value.length > 0) {
      const filtered = floridaCities.filter(city =>
        city.toLowerCase().startsWith(value.toLowerCase())
      );
      setCitySuggestions(filtered);
      setShowCitySuggestions(filtered.length > 0);
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
    
    if (errors.city) {
      setErrors(prev => ({ ...prev, city: '' }));
    }
  };

  const selectCity = (city) => {
    setFormData(prev => ({ ...prev, city }));
    setShowCitySuggestions(false);
    setCitySuggestions([]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, resume_file: 'File size must be less than 5MB' }));
        return;
      }
      
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, resume_file: 'Only PDF, DOC, and DOCX files are allowed' }));
        return;
      }
      
      setFormData(prev => ({ ...prev, resume_file: file }));
      setFileName(file.name);
      if (errors.resume_file) {
        setErrors(prev => ({ ...prev, resume_file: '' }));
      }
    }
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      available_days: prev.available_days.includes(day)
        ? prev.available_days.filter(d => d !== day)
        : [...prev.available_days, day]
    }));
  };

  const handleLanguageToggle = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages_spoken: prev.languages_spoken.includes(lang)
        ? prev.languages_spoken.filter(l => l !== lang)
        : [...prev.languages_spoken, lang]
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Personal Info
        if (!formData.full_name.trim()) newErrors.full_name = t('required');
        if (!formData.email.trim()) newErrors.email = t('required');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = t('invalidEmail');
        if (!formData.phone.trim()) newErrors.phone = t('required');
        else {
          const digits = formData.phone.replace(/\D/g, '');
          if (digits.length !== 10) newErrors.phone = 'Phone number must be 10 digits';
        }
        if (!formData.city.trim()) newErrors.city = t('required');
        // Languages spoken validation
        if (formData.languages_spoken.includes('other') && !formData.other_language.trim()) {
          newErrors.other_language = t('required');
        }
        break;
      case 2: // Employment History
        if (!formData.most_recent_job.trim()) newErrors.most_recent_job = t('required');
        if (!formData.why_stopped_working.trim()) newErrors.why_stopped_working = t('required');
        if (formData.currently_working === null) newErrors.currently_working = t('required');
        if (formData.currently_working === true) {
          if (!formData.current_work_type) newErrors.current_work_type = t('required');
          if (!formData.current_work_days.trim()) newErrors.current_work_days = t('required');
          if (!formData.looking_for) newErrors.looking_for = t('required');
        }
        if (!formData.position_applying) newErrors.position_applying = t('required');
        if (!formData.how_did_you_hear) newErrors.how_did_you_hear = t('required');
        break;
      case 3: // Authorization & Background
        if (!formData.work_authorization) newErrors.work_authorization = t('required');
        if (formData.clean_background === null) newErrors.clean_background = t('required');
        break;
      case 4: // Experience & Skills
        if (formData.has_prior_experience === null) newErrors.has_prior_experience = t('required');
        if (formData.has_prior_experience === true && !formData.experience_description.trim()) {
          newErrors.experience_description = t('required');
        }
        break;
      case 5: // Availability & Start Date
        if (!formData.when_can_start) newErrors.when_can_start = t('required');
        if (formData.when_can_start === 'custom' && !formData.start_date.trim()) {
          newErrors.start_date = t('required');
        }
        // Days and hours are optional
        break;
      case 6: // Transportation & Preferences
        if (formData.has_transportation === null) newErrors.has_transportation = t('required');
        if (formData.can_work_weekends === null) newErrors.can_work_weekends = t('required');
        if (formData.available_full_time === null) newErrors.available_full_time = t('required');
        break;
      case 7: // Additional Info
        // All optional
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setSubmitting(true);
    
    // Log submission attempt
    const submissionId = ErrorTracker.logSubmissionAttempt(formData, 'started');
    
    try {
      // Prepare availability data
      let availableDaysString = '';
      if (formData.days_option === 'all') {
        availableDaysString = 'All Days';
      } else if (formData.available_days.length > 0) {
        availableDaysString = formData.available_days.join(', ');
      }

      let availableHoursString = '';
      if (formData.hours_option === 'full-time') {
        availableHoursString = 'Full-time';
      } else if (formData.hours_option === 'part-time') {
        availableHoursString = 'Part-time';
      } else if (formData.hours_option === 'custom') {
        availableHoursString = formData.available_hours;
      }

      let startDateString = '';
      if (formData.when_can_start === 'immediately') {
        startDateString = 'Immediately';
      } else if (formData.when_can_start === 'two-weeks') {
        startDateString = 'In 2 weeks';
      } else if (formData.when_can_start === 'custom') {
        startDateString = formData.start_date;
      }

      // Prepare languages spoken string
      let languagesString = '';
      if (formData.languages_spoken.length > 0) {
        const langLabels = {
          'english': 'English',
          'spanish': 'Spanish',
          'other': formData.other_language || 'Other'
        };
        languagesString = formData.languages_spoken.map(lang => langLabels[lang] || lang).join(', ');
      }

      // Update language preference before submitting
      const submitData = {
        // Step 1: Personal Info
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        languages_spoken: languagesString,
        
        // Step 2: Employment History
        most_recent_job: formData.most_recent_job,
        why_stopped_working: formData.why_stopped_working,
        currently_working: formData.currently_working,
        current_work_type: formData.current_work_type,
        current_work_days: formData.current_work_days,
        looking_for: formData.looking_for,
        position_applying: formData.position_applying,
        how_did_you_hear: formData.how_did_you_hear,
        
        // Step 3: Authorization & Background
        work_authorization: formData.work_authorization,
        clean_background: formData.clean_background,
        
        // Step 4: Experience & Skills
        has_prior_experience: formData.has_prior_experience,
        experience_description: formData.experience_description,
        
        // Step 5: Availability & Start Date
        available_days: availableDaysString,
        available_hours: availableHoursString,
        when_can_start: startDateString,
        
        // Step 6: Transportation & Preferences
        has_transportation: formData.has_transportation,
        can_work_weekends: formData.can_work_weekends,
        available_full_time: formData.available_full_time,
        
        // Step 7: Additional
        additional_info: formData.additional_info,
        
        // Language preference
        preferred_language: language
      };

      // Make API request with timeout
      const response = await axios.post(`${API}/applications`, submitData, {
        timeout: 30000 // 30 second timeout
      });
      
      // Log successful submission
      ErrorTracker.updateSubmissionStatus(submissionId, 'success');
      
      toast.success(t('submitSuccess'));
      
      // Navigate to success page
      navigate('/success');
    } catch (error) {
      console.error('Error submitting application:', error);
      
      // Log the error with context
      ErrorTracker.logError(error, {
        type: 'submission_failed',
        email: formData.email,
        name: formData.full_name,
        step: 'final_submission',
        httpStatus: error.response?.status,
        httpMessage: error.response?.data
      });
      
      // Update submission status to failed
      ErrorTracker.updateSubmissionStatus(submissionId, 'failed', error);
      
      // Save failed submission for recovery
      ErrorTracker.saveFailedSubmission(formData, error);
      
      // Show detailed error message
      let errorMessage = t('submitError');
      if (error.code === 'ECONNABORTED') {
        errorMessage = t('submitTimeout') || 'Submission timed out. Your application has been saved and you can try again.';
      } else if (error.response?.status === 422) {
        errorMessage = t('submitValidation') || 'Please check all required fields are filled correctly.';
      } else if (!navigator.onLine) {
        errorMessage = t('submitOffline') || 'No internet connection. Your application has been saved and will be submitted when connection is restored.';
      }
      
      toast.error(errorMessage, {
        duration: 5000,
        action: {
          label: 'View Details',
          onClick: () => {
            console.log('Error details:', ErrorTracker.exportLogs());
          }
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Removed days array - no longer needed for checkboxes

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      {/* Header */}
      <div className="max-w-3xl mx-auto px-4 mb-6">
        <div className="flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
            data-testid="back-to-careers"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('backToCareers')}</span>
          </button>
          <LanguageSelector />
        </div>
      </div>

      {/* Application Form */}
      <div className="max-w-3xl mx-auto px-4">
        {/* Recovery Prompt */}
        {showRecoveryPrompt && failedSubmission && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-900 mb-1">
                  {t('previousSubmissionFound') || 'Previous Submission Found'}
                </h3>
                <p className="text-sm text-yellow-800 mb-3">
                  {t('previousSubmissionMessage') || `We found a previous submission from ${new Date(failedSubmission.timestamp).toLocaleString()} that didn't complete. Would you like to recover it?`}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleRecoverSubmission}
                    className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    {t('recoverSubmission') || 'Recover Submission'}
                  </button>
                  <button
                    onClick={handleDismissRecovery}
                    className="px-4 py-2 bg-white text-yellow-900 text-sm font-medium rounded-lg border border-yellow-300 hover:bg-yellow-50 transition-colors"
                  >
                    {t('startNew') || 'Start New'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow-2xl rounded-2xl overflow-hidden border border-slate-100">
          {/* Header */}
          <div className="bg-slate-50 p-6 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Briefcase className="w-6 h-6 text-blue-600" />
              <h1 className="text-2xl font-bold text-slate-900" data-testid="application-title">
                {t('applicationTitle')}
              </h1>
            </div>
          </div>

          {/* Stepper */}
          <div className="p-8 pb-4">
            <Stepper currentStep={currentStep} totalSteps={7} />
          </div>

          {/* Form Body */}
          <div className="p-8 space-y-6">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="space-y-4" data-testid="step-1">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{t('step1')}</h2>
                
                <div>
                  <Label htmlFor="full_name">{t('fullName')}</Label>
                  <Input
                    id="full_name"
                    placeholder={t('fullNamePlaceholder')}
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    className={errors.full_name ? 'border-red-500' : ''}
                    data-testid="input-full-name"
                  />
                  {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
                </div>

                <div>
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={errors.email ? 'border-red-500' : ''}
                    data-testid="input-email"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <Label htmlFor="phone">{t('phone')}</Label>
                  <Input
                    id="phone"
                    placeholder="(___) ___-____"
                    value={formData.phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    className={errors.phone ? 'border-red-500' : ''}
                    data-testid="input-phone"
                    maxLength="14"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div className="relative">
                  <Label htmlFor="city">{t('city')}</Label>
                  <Input
                    id="city"
                    placeholder={t('cityPlaceholder')}
                    value={formData.city}
                    onChange={(e) => handleCityChange(e.target.value)}
                    onBlur={() => setTimeout(() => setShowCitySuggestions(false), 300)}
                    onFocus={() => {
                      if (formData.city) {
                        const filtered = floridaCities.filter(c => c.toLowerCase().startsWith(formData.city.toLowerCase()));
                        setCitySuggestions(filtered);
                        setShowCitySuggestions(filtered.length > 0);
                      }
                    }}
                    className={errors.city ? 'border-red-500' : ''}
                    data-testid="input-city"
                    autoComplete="off"
                  />
                  {showCitySuggestions && citySuggestions.length > 0 && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowCitySuggestions(false)}
                      />
                      <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {citySuggestions.map((city, index) => (
                          <div
                            key={index}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectCity(city);
                            }}
                            data-testid={`city-suggestion-${index}`}
                          >
                            {city}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                </div>

                {/* Languages Spoken */}
                <div>
                  <Label className="mb-2 block">{t('languagesSpoken')}</Label>
                  <p className="text-sm text-slate-500 mb-3">{t('languagesSpokenHint')}</p>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="lang-english"
                        checked={formData.languages_spoken.includes('english')}
                        onCheckedChange={() => handleLanguageToggle('english')}
                        data-testid="checkbox-lang-english"
                      />
                      <label
                        htmlFor="lang-english"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {t('languageEnglish')}
                      </label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="lang-spanish"
                        checked={formData.languages_spoken.includes('spanish')}
                        onCheckedChange={() => handleLanguageToggle('spanish')}
                        data-testid="checkbox-lang-spanish"
                      />
                      <label
                        htmlFor="lang-spanish"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {t('languageSpanish')}
                      </label>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="lang-other"
                          checked={formData.languages_spoken.includes('other')}
                          onCheckedChange={() => handleLanguageToggle('other')}
                          data-testid="checkbox-lang-other"
                        />
                        <label
                          htmlFor="lang-other"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {t('languageOther')}
                        </label>
                      </div>
                      
                      {formData.languages_spoken.includes('other') && (
                        <div className="ml-6">
                          <Input
                            id="other_language"
                            placeholder={t('otherLanguagePlaceholder')}
                            value={formData.other_language}
                            onChange={(e) => handleInputChange('other_language', e.target.value)}
                            className={errors.other_language ? 'border-red-500' : ''}
                            data-testid="input-other-language"
                          />
                          {errors.other_language && <p className="text-red-500 text-sm mt-1">{errors.other_language}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Employment History */}
            {currentStep === 2 && (
              <div className="space-y-6" data-testid="step-2">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{t('step2')}</h2>
                
                <div>
                  <Label htmlFor="most_recent_job">{t('mostRecentJob')}</Label>
                  <Input
                    id="most_recent_job"
                    placeholder={t('mostRecentJobPlaceholder')}
                    value={formData.most_recent_job}
                    onChange={(e) => handleInputChange('most_recent_job', e.target.value)}
                    className={errors.most_recent_job ? 'border-red-500' : ''}
                    data-testid="input-most-recent-job"
                  />
                  {errors.most_recent_job && <p className="text-red-500 text-sm mt-1">{errors.most_recent_job}</p>}
                </div>

                <div>
                  <Label htmlFor="why_stopped_working">{t('whyStoppedWorking')}</Label>
                  <Textarea
                    id="why_stopped_working"
                    placeholder={t('whyStoppedPlaceholder')}
                    value={formData.why_stopped_working}
                    onChange={(e) => handleInputChange('why_stopped_working', e.target.value)}
                    rows={3}
                    className={errors.why_stopped_working ? 'border-red-500' : ''}
                    data-testid="textarea-why-stopped"
                  />
                  {errors.why_stopped_working && <p className="text-red-500 text-sm mt-1">{errors.why_stopped_working}</p>}
                </div>

                <div>
                  <Label className="mb-3 block">{t('currentlyWorking')}</Label>
                  <RadioGroup
                    value={formData.currently_working === null ? '' : formData.currently_working.toString()}
                    onValueChange={(value) => handleInputChange('currently_working', value === 'true')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="currently-yes" data-testid="radio-currently-yes" />
                      <Label htmlFor="currently-yes" className="font-normal cursor-pointer">{t('yes')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="currently-no" data-testid="radio-currently-no" />
                      <Label htmlFor="currently-no" className="font-normal cursor-pointer">{t('no')}</Label>
                    </div>
                  </RadioGroup>
                  {errors.currently_working && <p className="text-red-500 text-sm mt-1">{errors.currently_working}</p>}
                </div>

                {formData.currently_working === true && (
                  <>
                    <div>
                      <Label className="mb-3 block">{t('currentWorkType')}</Label>
                      <RadioGroup
                        value={formData.current_work_type}
                        onValueChange={(value) => handleInputChange('current_work_type', value)}
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="full-time" id="current-full" data-testid="radio-current-full" />
                          <Label htmlFor="current-full" className="font-normal cursor-pointer">{t('currentFullTime')}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="part-time" id="current-part" data-testid="radio-current-part" />
                          <Label htmlFor="current-part" className="font-normal cursor-pointer">{t('currentPartTime')}</Label>
                        </div>
                      </RadioGroup>
                      {errors.current_work_type && <p className="text-red-500 text-sm mt-1">{errors.current_work_type}</p>}
                    </div>

                    <div>
                      <Label htmlFor="current_work_days">{t('currentWorkDays')}</Label>
                      <Input
                        id="current_work_days"
                        placeholder={t('currentWorkDaysPlaceholder')}
                        value={formData.current_work_days}
                        onChange={(e) => handleInputChange('current_work_days', e.target.value)}
                        className={errors.current_work_days ? 'border-red-500' : ''}
                        data-testid="input-current-work-days"
                      />
                      {errors.current_work_days && <p className="text-red-500 text-sm mt-1">{errors.current_work_days}</p>}
                    </div>

                    <div>
                      <Label className="mb-3 block">{t('lookingFor')}</Label>
                      <RadioGroup
                        value={formData.looking_for}
                        onValueChange={(value) => handleInputChange('looking_for', value)}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="part-time-additional" id="looking-part" data-testid="radio-looking-part" />
                          <Label htmlFor="looking-part" className="font-normal cursor-pointer">{t('lookingForPartTime')}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="full-time-additional" id="looking-full" data-testid="radio-looking-full" />
                          <Label htmlFor="looking-full" className="font-normal cursor-pointer">{t('lookingForFullTime')}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="switch" id="looking-switch" data-testid="radio-looking-switch" />
                          <Label htmlFor="looking-switch" className="font-normal cursor-pointer">{t('lookingForSwitch')}</Label>
                        </div>
                      </RadioGroup>
                      {errors.looking_for && <p className="text-red-500 text-sm mt-1">{errors.looking_for}</p>}
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="position_applying">{t('positionApplying')}</Label>
                  <Input
                    id="position_applying"
                    placeholder={t('positionPlaceholder')}
                    value={formData.position_applying}
                    onChange={(e) => handleInputChange('position_applying', e.target.value)}
                    className={errors.position_applying ? 'border-red-500' : ''}
                    data-testid="input-position"
                  />
                  {errors.position_applying && <p className="text-red-500 text-sm mt-1">{errors.position_applying}</p>}
                </div>

                <div>
                  <Label htmlFor="how_did_you_hear">{t('howDidYouHear')}</Label>
                  <Select
                    value={formData.how_did_you_hear}
                    onValueChange={(value) => handleInputChange('how_did_you_hear', value)}
                  >
                    <SelectTrigger className={errors.how_did_you_hear ? 'border-red-500' : ''} data-testid="select-source">
                      <SelectValue placeholder={t('howDidYouHear')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online" data-testid="source-online">{t('sourceOnline')}</SelectItem>
                      <SelectItem value="referral" data-testid="source-referral">{t('sourceReferral')}</SelectItem>
                      <SelectItem value="social" data-testid="source-social">{t('sourceSocial')}</SelectItem>
                      <SelectItem value="other" data-testid="source-other">{t('sourceOther')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.how_did_you_hear && <p className="text-red-500 text-sm mt-1">{errors.how_did_you_hear}</p>}
                </div>
              </div>
            )}

            {/* Step 3: Work Authorization & Background */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="step-3">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{t('step3')}</h2>
                
                <div>
                  <Label className="mb-3 block">{t('workAuthorization')}</Label>
                  <RadioGroup
                    value={formData.work_authorization}
                    onValueChange={(value) => handleInputChange('work_authorization', value)}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="citizen" id="auth-citizen" data-testid="radio-auth-citizen" />
                      <Label htmlFor="auth-citizen" className="font-normal cursor-pointer">{t('usCitizen')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="permanent_resident" id="auth-resident" data-testid="radio-auth-resident" />
                      <Label htmlFor="auth-resident" className="font-normal cursor-pointer">{t('permanentResident')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="work_permit" id="auth-permit" data-testid="radio-auth-permit" />
                      <Label htmlFor="auth-permit" className="font-normal cursor-pointer">{t('workPermit')}</Label>
                    </div>
                  </RadioGroup>
                  {errors.work_authorization && <p className="text-red-500 text-sm mt-1">{errors.work_authorization}</p>}
                </div>

                <div>
                  <Label className="mb-2 block">{t('cleanBackground')}</Label>
                  <p className="text-sm text-slate-500 mb-3">{t('cleanBackgroundHint')}</p>
                  <RadioGroup
                    value={formData.clean_background === null ? '' : formData.clean_background.toString()}
                    onValueChange={(value) => handleInputChange('clean_background', value === 'true')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="background-yes" data-testid="radio-background-yes" />
                      <Label htmlFor="background-yes" className="font-normal cursor-pointer">{t('yes')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="background-no" data-testid="radio-background-no" />
                      <Label htmlFor="background-no" className="font-normal cursor-pointer">{t('no')}</Label>
                    </div>
                  </RadioGroup>
                  {errors.clean_background && <p className="text-red-500 text-sm mt-1">{errors.clean_background}</p>}
                </div>
              </div>
            )}

            {/* Step 4: Experience & Skills */}
            {currentStep === 4 && (
              <div className="space-y-6" data-testid="step-4">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{t('step4')}</h2>
                
                <div>
                  <Label className="mb-3 block">{t('hasPriorExperience')}</Label>
                  <RadioGroup
                    value={formData.has_prior_experience === null ? '' : formData.has_prior_experience.toString()}
                    onValueChange={(value) => handleInputChange('has_prior_experience', value === 'true')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="exp-yes" data-testid="radio-experience-yes" />
                      <Label htmlFor="exp-yes" className="font-normal cursor-pointer">{t('yes')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="exp-no" data-testid="radio-experience-no" />
                      <Label htmlFor="exp-no" className="font-normal cursor-pointer">{t('no')}</Label>
                    </div>
                  </RadioGroup>
                  {errors.has_prior_experience && <p className="text-red-500 text-sm mt-1">{errors.has_prior_experience}</p>}
                </div>

                {formData.has_prior_experience === true && (
                  <div>
                    <Label htmlFor="experience_description">{t('experienceDescription')}</Label>
                    <Textarea
                      id="experience_description"
                      placeholder={t('experienceDescriptionPlaceholder')}
                      value={formData.experience_description}
                      onChange={(e) => handleInputChange('experience_description', e.target.value)}
                      rows={6}
                      className={`resize-none ${errors.experience_description ? 'border-red-500' : ''}`}
                      data-testid="textarea-experience"
                    />
                    {errors.experience_description && <p className="text-red-500 text-sm mt-1">{errors.experience_description}</p>}
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Availability & Start Date */}
            {currentStep === 5 && (
              <div className="space-y-6" data-testid="step-5">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{t('step5')}</h2>
                
                {/* Available Days */}
                <div>
                  <Label className="mb-3 block">{t('availableDays')}</Label>
                  <RadioGroup
                    value={formData.days_option}
                    onValueChange={(value) => handleInputChange('days_option', value)}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id="days-all" data-testid="radio-days-all" />
                      <Label htmlFor="days-all" className="font-normal cursor-pointer">{t('allDays')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="specific" id="days-specific" data-testid="radio-days-specific" />
                      <Label htmlFor="days-specific" className="font-normal cursor-pointer">{t('selectSpecificDays')}</Label>
                    </div>
                  </RadioGroup>
                  
                  {formData.days_option === 'specific' && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <div key={day} className="flex items-center space-x-2">
                          <Checkbox
                            id={day}
                            checked={formData.available_days.includes(day)}
                            onCheckedChange={() => handleDayToggle(day)}
                            data-testid={`checkbox-${day}`}
                          />
                          <label
                            htmlFor={day}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {t(day)}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.available_days && <p className="text-red-500 text-sm mt-1">{errors.available_days}</p>}
                </div>

                {/* Available Hours */}
                <div>
                  <Label className="mb-3 block">{t('availableHours')}</Label>
                  <RadioGroup
                    value={formData.hours_option}
                    onValueChange={(value) => handleInputChange('hours_option', value)}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="full-time" id="hours-fulltime" data-testid="radio-hours-fulltime" />
                      <Label htmlFor="hours-fulltime" className="font-normal cursor-pointer">{t('fullTime')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="part-time" id="hours-parttime" data-testid="radio-hours-parttime" />
                      <Label htmlFor="hours-parttime" className="font-normal cursor-pointer">{t('partTime')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="custom" id="hours-custom" data-testid="radio-hours-custom" />
                      <Label htmlFor="hours-custom" className="font-normal cursor-pointer">{t('customHours')}</Label>
                    </div>
                  </RadioGroup>
                  
                  {formData.hours_option === 'custom' && (
                    <div className="mt-4">
                      <Input
                        id="custom_hours"
                        placeholder={t('customHoursPlaceholder')}
                        value={formData.available_hours}
                        onChange={(e) => handleInputChange('available_hours', e.target.value)}
                        className={errors.available_hours ? 'border-red-500' : ''}
                        data-testid="input-custom-hours"
                      />
                    </div>
                  )}
                  {errors.available_hours && <p className="text-red-500 text-sm mt-1">{errors.available_hours}</p>}
                </div>

                {/* When Can Start */}
                <div>
                  <Label className="mb-3 block">{t('whenCanStart')}</Label>
                  <RadioGroup
                    value={formData.when_can_start}
                    onValueChange={(value) => handleInputChange('when_can_start', value)}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="immediately" id="start-now" data-testid="radio-start-immediately" />
                      <Label htmlFor="start-now" className="font-normal cursor-pointer">{t('startImmediately')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="two-weeks" id="start-two-weeks" data-testid="radio-start-two-weeks" />
                      <Label htmlFor="start-two-weeks" className="font-normal cursor-pointer">{t('startTwoWeeks')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="custom" id="start-custom" data-testid="radio-start-custom" />
                      <Label htmlFor="start-custom" className="font-normal cursor-pointer">{t('startCustom')}</Label>
                    </div>
                  </RadioGroup>
                  
                  {formData.when_can_start === 'custom' && (
                    <div className="mt-4">
                      <Input
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleInputChange('start_date', e.target.value)}
                        className={errors.start_date ? 'border-red-500' : ''}
                        data-testid="input-start-date"
                      />
                    </div>
                  )}
                  {errors.when_can_start && <p className="text-red-500 text-sm mt-1">{errors.when_can_start}</p>}
                  {errors.start_date && <p className="text-red-500 text-sm mt-1">{errors.start_date}</p>}
                </div>
              </div>
            )}

            {/* Step 6: Transportation & Preferences */}
            {currentStep === 6 && (
              <div className="space-y-6" data-testid="step-6">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{t('step6')}</h2>
                
                <div>
                  <Label className="mb-2 block">{t('hasTransportation')}</Label>
                  <RadioGroup
                    value={formData.has_transportation === null ? '' : formData.has_transportation.toString()}
                    onValueChange={(value) => handleInputChange('has_transportation', value === 'true')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="transport-yes" data-testid="radio-transport-yes" />
                      <Label htmlFor="transport-yes" className="font-normal cursor-pointer">{t('yes')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="transport-no" data-testid="radio-transport-no" />
                      <Label htmlFor="transport-no" className="font-normal cursor-pointer">{t('no')}</Label>
                    </div>
                  </RadioGroup>
                  {errors.has_transportation && <p className="text-red-500 text-sm mt-1">{errors.has_transportation}</p>}
                </div>

                <div>
                  <Label className="mb-2 block">{t('canWorkWeekends')}</Label>
                  <RadioGroup
                    value={formData.can_work_weekends === null ? '' : formData.can_work_weekends.toString()}
                    onValueChange={(value) => handleInputChange('can_work_weekends', value === 'true')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="weekends-yes" data-testid="radio-weekends-yes" />
                      <Label htmlFor="weekends-yes" className="font-normal cursor-pointer">{t('yes')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="weekends-no" data-testid="radio-weekends-no" />
                      <Label htmlFor="weekends-no" className="font-normal cursor-pointer">{t('no')}</Label>
                    </div>
                  </RadioGroup>
                  {errors.can_work_weekends && <p className="text-red-500 text-sm mt-1">{errors.can_work_weekends}</p>}
                </div>

                <div>
                  <Label className="mb-2 block">{t('availableFullTime')}</Label>
                  <RadioGroup
                    value={formData.available_full_time === null ? '' : formData.available_full_time.toString()}
                    onValueChange={(value) => handleInputChange('available_full_time', value === 'true')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="fulltime-yes" data-testid="radio-fulltime-yes" />
                      <Label htmlFor="fulltime-yes" className="font-normal cursor-pointer">{t('yes')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="fulltime-no" data-testid="radio-fulltime-no" />
                      <Label htmlFor="fulltime-no" className="font-normal cursor-pointer">{t('no')}</Label>
                    </div>
                  </RadioGroup>
                  {errors.available_full_time && <p className="text-red-500 text-sm mt-1">{errors.available_full_time}</p>}
                </div>
              </div>
            )}

            {/* Step 7: Additional Information & Resume */}
            {currentStep === 7 && (
              <div className="space-y-6" data-testid="step-7">
                <h2 className="text-xl font-bold text-slate-900 mb-4">{t('step7')}</h2>
                
                <div>
                  <Label htmlFor="additional_info">{t('additionalInfo')}</Label>
                  <Textarea
                    id="additional_info"
                    placeholder={t('additionalInfoPlaceholder')}
                    value={formData.additional_info}
                    onChange={(e) => handleInputChange('additional_info', e.target.value)}
                    rows={6}
                    className="resize-none"
                    data-testid="textarea-additional-info"
                  />
                </div>

                <div>
                  <Label htmlFor="resume_file">{t('resumeUpload')}</Label>
                  <p className="text-sm text-slate-500 mb-2">{t('resumeUploadHint')}</p>
                  <div className="flex items-center gap-3">
                    <label 
                      htmlFor="resume_file" 
                      className="cursor-pointer bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-medium py-2 px-4 rounded-lg transition-colors inline-flex items-center gap-2"
                      data-testid="file-upload-label"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('chooseFile')}
                    </label>
                    <input
                      id="resume_file"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="file-input-resume"
                    />
                    <span className="text-sm text-slate-600" data-testid="file-name-display">
                      {fileName || t('noFileChosen')}
                    </span>
                  </div>
                  {errors.resume_file && <p className="text-red-500 text-sm mt-1">{errors.resume_file}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-6 py-2 text-slate-600 hover:text-blue-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="button-back"
            >
              {t('back')}
            </button>
            
            {currentStep < 7 ? (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-lg hover:shadow-blue-500/30 transition-all"
                data-testid="button-next"
              >
                {t('next')}
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-lg hover:shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="button-submit"
              >
                {submitting ? t('submitting') : t('submit')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationPage;